import { useCallback, useMemo, useState } from "react";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import Box from "ds/components/Box";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields, AssignRoleFormEditFields } from "components/AssignRoleForm/types";
import { CREATE_FORM_ID } from "components/AssignRoleForm/constants";
import useStackRoleBindings from "shared/Stack/Roles/useStackRoleBindings";
import useStackRoleBindingBatchCreate from "shared/Stack/Roles/useStackRoleBindingBatchCreate";
import useStackRoleBindingUpdate from "shared/Stack/Roles/useStackRoleBindingUpdate";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { pluralize } from "shared/Pluralize";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import { showStackRoleUnassignConfirmation } from "shared/Stack/Roles/UnassignConfirmation";

type ManageRolesDrawerProps = {
  stackId: string;
};

const refetchQueries = ["StackRoleBindingsWithRoles"];

const ManageRolesDrawer = createDrawer(({ stackId }: ManageRolesDrawerProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageStack.StackSettingsRoles,
  });

  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({
    [CREATE_FORM_ID]: false,
  });

  const setIsDirtyHandler = useCallback(
    (formId: string, value: boolean) => setDirtyFields((prev) => ({ ...prev, [formId]: value })),
    []
  );

  const { stackRoleBindings, roles, loading } = useStackRoleBindings({
    stackID: stackId,
  });

  const assignedRoles = useMemo(
    () =>
      stackRoleBindings.map((binding) => ({
        id: binding.id,
        roleID: binding.role.id,
        spaceID: binding.space.id,
        spaceName: binding.space.name,
      })),
    [stackRoleBindings]
  );

  const [batchCreateStackRoleBinding, { loading: createBatchRoleBindingLoading }] =
    useStackRoleBindingBatchCreate({
      refetchQueries,
    });

  const onCreate = useCallback(
    (formData: AssignRoleFormAddFields) => {
      const roleBindings = formData.roleIds.map((roleId) => ({
        roleID: roleId,
        spaceID: formData.space,
        stackID: stackId,
      }));

      return batchCreateStackRoleBinding(
        {
          input: {
            bindings: roleBindings,
          },
        },
        (data) => {
          if (data?.stackRoleBindingBatchCreate?.length) {
            const count = data.stackRoleBindingBatchCreate.length;
            reportSuccess({
              message: `${pluralize("Role", count)} successfully assigned`,
            });
            trackSegmentEvent("Stack role binding added", { count });
          }
        }
      );
    },
    [batchCreateStackRoleBinding, stackId, reportSuccess, trackSegmentEvent]
  );

  const [updateStackRoleBinding] = useStackRoleBindingUpdate({
    refetchQueries,
  });

  const onEdit = useCallback(
    (formData: AssignRoleFormEditFields, roleAssignmentId: string) => {
      updateStackRoleBinding(
        {
          id: roleAssignmentId,
          input: {
            roleID: formData.roleId,
            spaceID: formData.space,
          },
        },
        (data) => {
          if (data?.stackRoleBindingUpdate?.id) {
            reportSuccess({
              message: "Role assignment successfully updated",
            });
            trackSegmentEvent("Stack role binding updated");
            setIsDirtyHandler(roleAssignmentId, false);
          }
        }
      );
    },
    [updateStackRoleBinding, setIsDirtyHandler, reportSuccess, trackSegmentEvent]
  );

  const availableRoles = useMemo(() => roles, [roles]);
  const isDirty = useMemo(() => !!Object.values(dirtyFields).filter(Boolean).length, [dirtyFields]);

  const onDelete = useCallback(
    (roleAssignmentId: string, roleName: string) =>
      showStackRoleUnassignConfirmation({
        id: roleAssignmentId,
        name: roleName,
        refetchQueries,
        source: "drawer",
      }),
    []
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Manage roles" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody>
        <Box direction="column" gap="large">
          <AssignRoleForm
            onCreate={onCreate}
            onEdit={onEdit}
            setDirtyFields={setIsDirtyHandler}
            availableRoles={availableRoles}
            assignedRoles={assignedRoles}
            loading={loading}
            createLoading={createBatchRoleBindingLoading}
            onDelete={onDelete}
          />
        </Box>
      </DrawerBody>
    </DrawerForm>
  );
});

export const showManageRolesDrawer = createDrawerTrigger(ManageRolesDrawer);
